// Hooks TanStack Query — le calendrier de conservation (/api/calendrier-conservation).
//
// FORME : recopiee du contrat serveur `apps/admin/server/services/calendrierConservation/contrat.ts`,
// qui la VALIDE par Zod a l ecriture et a la lecture. Recopiee et non importee : le projet
// client a `rootDir: ./src` et ne peut pas inclure un fichier serveur, meme pour un type. Un
// champ ajoute au contrat s ajoute ici.
//
// Cle de cache SANS companyId, et c est voulu : le calendrier est un referentiel GLOBAL
// (ni company_id ni RLS), reserve a l administrateur d organisation.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';

export type NatureDeSource = 'officielle' | 'guide' | 'recherche';

export interface SourceCitee {
  title: string;
  url: string | null;
  consultedOn: string;
  nature: NatureDeSource;
  draftedBefore2025: boolean;
}

export type FinDeConservation = 'detruire' | 'anonymiser';

export interface LigneDuCalendrier {
  id: string;
  categoryCode: string;
  jurisdictionCode: string;
  regimeCode: string | null;
  /** NULL = « sans terme fixe - juriste » : la ligne ne detruit jamais. */
  durationMonths: number | null;
  triggerEvent: string;
  endAction: FinDeConservation;
  legalBasis: string | null;
  sources: SourceCitee[];
  rationale: string | null;
  status: 'redigee' | 'validee';
  validatedByRole: string | null;
  validatedDate: string | null;
  validationReference: string | null;
  effectiveDate: string;
  isActive: boolean;
  version: number;
  updatedAt: string;
}

export type GenreDeChangement = 'creation' | 'modification' | 'validation' | 'retrait' | 'reprise';

export interface ChangementDuCalendrier {
  id: string;
  changeKind: GenreDeChangement;
  effectiveDate: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown>;
  reason: string;
  changedBy: string;
  changedAt: string;
}

export interface VocabulaireDuCalendrier {
  classes: { code: string; description: string }[];
  triggers: string[];
  jurisdictions: { code: string; nameFr: string; nameEn: string }[];
  regimes: { code: string; jurisdictionCode: string; nameFr: string; nameEn: string }[];
}

/** Les lignes redigees d une province qui n est pas encore ouverte dans la dimension des juridictions. */
export interface EnAttenteDeProvince {
  jurisdictionCode: string;
  lignes: number;
}

/** Ce que le calendrier redige par l agent ajouterait (bouton « Charger le calendrier redige »). */
export interface EtatDuCalendrierRedige {
  total: number;
  aCharger: number;
  enAttenteDeProvince: EnAttenteDeProvince[];
}

export interface BilanDuPrechargement {
  ajoutees: number;
  dejaPresentes: number;
  enAttenteDeProvince: EnAttenteDeProvince[];
}

export interface CalendrierDeConservation {
  lignes: LigneDuCalendrier[];
  vocabulaire: VocabulaireDuCalendrier;
  /** Absent d un serveur anterieur au chargement du calendrier redige : la page n offre alors pas le bouton. */
  redige?: EtatDuCalendrierRedige;
}

/** La regle d une ligne, et l evenement qui la change (date d effet, raison). */
export interface RegleEtEvenement {
  durationMonths: number | null;
  triggerEvent: string;
  endAction: FinDeConservation;
  legalBasis: string | null;
  sources: SourceCitee[];
  rationale: string | null;
  effectiveDate: string;
  reason: string;
}

export interface Creation extends RegleEtEvenement {
  categoryCode: string;
  jurisdictionCode: string;
  regimeCode: string | null;
}

export interface Modification extends RegleEtEvenement {
  version: number;
}

export interface Bascule {
  version: number;
  effectiveDate: string;
  reason: string;
}

const BASE = '/api/calendrier-conservation';
export const CALENDRIER_QUERY_KEY = ['calendrier-conservation'] as const;

export function useCalendrierConservation() {
  const { user } = useAuth();
  return useQuery({
    queryKey: CALENDRIER_QUERY_KEY,
    queryFn: () => api.get<CalendrierDeConservation>(BASE),
    enabled: !!user,
  });
}

export function useChangementsDuCalendrier(id: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...CALENDRIER_QUERY_KEY, 'changements', id],
    queryFn: () => api.get<{ changements: ChangementDuCalendrier[] }>(`${BASE}/${id}/changements`),
    enabled: !!user && id !== null,
  });
}

/** Chaque ecriture recharge le calendrier ET le journal de la ligne. */
function useEcriture<V>(ecrire: (v: V) => Promise<{ ligne: LigneDuCalendrier }>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ecrire,
    onSuccess: () => client.invalidateQueries({ queryKey: CALENDRIER_QUERY_KEY }),
  });
}

export function useCreerLigne() {
  return useEcriture((c: Creation) => api.post<{ ligne: LigneDuCalendrier }>(BASE, c));
}

export function useModifierLigne() {
  return useEcriture(({ id, ...m }: Modification & { id: string }) =>
    api.patch<{ ligne: LigneDuCalendrier }>(`${BASE}/${id}`, m),
  );
}

/** Charge les lignes absentes du calendrier redige, chacune `redigee` avec son evenement date. */
export function usePrechargerLeCalendrierRedige() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (effectiveDate: string) => api.post<BilanDuPrechargement>(`${BASE}/prechargement`, { effectiveDate }),
    onSuccess: () => client.invalidateQueries({ queryKey: CALENDRIER_QUERY_KEY }),
  });
}

export function useBasculerLigne() {
  return useEcriture(({ id, genre, ...b }: Bascule & { id: string; genre: 'retrait' | 'reprise' }) =>
    api.post<{ ligne: LigneDuCalendrier }>(`${BASE}/${id}/${genre}`, b),
  );
}
